import { immerable } from 'immer';

import { MetricWithDimensionFields } from '@/features/experiment/constants/analysis';

/* eslint-disable @typescript-eslint/ban-types */
/** Rollout represents a rollout definition. */
export interface Rollout {
  variable: string;
  version?: string;
  trackConf?: TrackConf;
  rolloutType?: RolloutType;
  cachedRolloutType?: RolloutType;
  rolloutBy?: string;
  rollouts?: RolloutTarget[];
  default?: RolloutTarget;
  schema?: number;
  assignBy?: string;
  startDate?: string;
  endDate?: string;
}
/** SuperExperiment represents the combination of AdaptiveExperiment and Experiment definition. */
export interface SuperExperiment {
  type?: string;
  domain: string;
  name: string;
  state?: string;
  variables: Variable[] | string[];
  constraints: Constraint[];
  owner?: string;
  isOverlap: boolean;
  created_at?: number;
  start_time?: number;
  end_time?: number;
  slotting?: Slotting;
  metadata?: ExperimentMetadata;
  customTimeSliceStrategy?: string;
  approvalFlow?: ApprovalFlow;
  regionalExperimentName?: string;
}

/** ExperimentViews represents a ExperimentViews definition. */
export class ExperimentViews {
  experiment_views: ExperimentView[];
  count: number;
}

/** ExperimentView represents a ExperimentView definition. */
export class ExperimentView {
  type: string;
  adaptive_experiment_model?: AdaptiveExperiment;
  experiment_model?: Experiment;
  created_at: number;
  start_time: number;
  end_time: number;
}

/** AdaptiveExperiment represents a AdaptiveExperiment definition. */
export class AdaptiveExperiment {
  id: number;
  name: string;
  experiment_template: ExperimentTemplate;
  control: ControlArm;
  exploitation_weight: number;
  variables_template: any;
  n_arms_per_trial: number;
  state: string;
  version: number;
  trial_duration_hours: number;
  search_space: any;
  objective: any;
  algorithm: any;
  owner: string;
  approval_flow: ApprovalFlow;
  created_from: string;
  start_time: number;
  end_time: number;
  tenant_seq: number;
}

export class ControlArm {
  arm_id: string;
  parameters: any;
}

export class ExperimentTemplate {
  domain: string;
  strategy: string;
  span_trial_seconds: number;
  variables: string[];
  facets: string[];
  constraints: Constraint[];
  targeting: Targeting[];
  is_group_of_groups: boolean;
  assign_by: string;
}

export class TrialMetrics {
  btr: any;
  eta_promise: any;
}

export class TrialParameters {
  w_bias: number;
  w_car: number;
  w_confidence: number;
  w_distance: number;
  w_eta: number;
  w_max: number;
  w_promotion: number;
}

export class AdaptiveTrial {
  adaptive_experiment_name: string;
  arm_id: string;
  arm_index: number;
  client: string;
  domain: string;
  id: number;
  metrics: TrialMetrics;
  parameters: TrialParameters;
  start_time?: number;
  end_time?: number;
  state: string;
  trial_name: string;
  type: string;
  weight: number;
}

export interface Variables {
  items: Variable[];
  offset: number;
  limit: number;
  total: number;
}

export interface Scope {
  count: number;
  facets: FacetView[];
}

export interface TrackConf {
  mode?: number;
  limit?: number;
}

export interface RolloutVersion {
  rollout: Rollout;
  createdAt: string;
  approver: string;
  creator: string;
  action: string;
}

export interface RolloutHistoryEntry {
  version: string;
  createdAt: string;
  approver: string;
  creator: string;
  action: string;
}

export interface RolloutHistory {
  variable: string;
  versions: RolloutHistoryEntry[];
}

export interface ValidationError {
  failure: string;
  violations: string[];
}

/** RolloutReview represents a rollout validation results. */
export interface RolloutReview {
  success: boolean;
  errors?: ValidationError[];
  warnings?: ValidationError[];
}

/** Choice represents a choice for an experiment. */
export class Choice {
  value: any;
  string?: string;
  script?: string;
  group?: string;
  range?: string;
  chance?: number;
  span?: number;
  spanBuffer?: number;
  id?: string;
  treatment?: number;
  name?: string;
}

/** Creates an empty choice. */
export function EmptyChoice(): Choice {
  return {
    string: '',
    value: 0,
    script: '',
  };
}

export class SliceAssignment {
  start: number;
  end: number;
  choice_index: number;
}

/** Variable is the experiment variable DTO. */
export class Variable {
  name: string;
  service?: string;
  strategy: string;
  geohashType: number | undefined;
  choices: Choice[];
  facets?: string[];
  domain?: string;
  sliceAssignments?: SliceAssignment[];
  techFamily?: string;
  [i: string]: any;
}

/** Generic constraint used for both experiments and rollouts. */
export class Constraint {
  target?: string;
  op?: string;
  value?: string;
  type?: ConstraintType;
  valueItemType?: string;
}

/** ConstraintType defines if constraint is a standard constraint or a CustomString or CustomInt. */
export enum ConstraintType {
  Standard = 'standard',
  Targeting = 'targeting',
  CustomString = 'customString',
  CustomInt = 'customInt',
}

/** Creates an empty constraint. */
export function EmptyConstraint(): Constraint {
  return {
    op: '',
    target: '',
    value: '',
    type: ConstraintType.Standard,
  };
}

/** Experiment is the main experiment DTO */
export class Experiment {
  name: string | undefined;
  children?: Experiment;
  variables?: Variable[];
  facets?: string[];
  constraints?: Constraint[];
  metrics?: string[];
  schemaVersion?: number;
  state?: string;
  owner?: string;
  domain?: string;
  slotting?: Slotting;
  metadata?: ExperimentMetadata;
  createdAt?: number;
  startTime?: number;
  endTime?: number;
  approvalFlow?: ApprovalFlow;
  autoAdaptive?: AutoAdaptive;
  schedulingDefinition?: SchedulingDef;
  targetBy?: string;
  customTimeSliceStrategy?: string;
  regionalExperimentName?: string;
}

export class RegionalExperimentConfigValues {
  variables: Variable[];
  country_code: string;
}

export class ExperimentWithAnalysisConfig {
  experiment: Experiment;
  analysisConfig: ExperimentAnalysisConfigView;
}

export class ExperimentWithAnalysisConfigV2 {
  experiment: Experiment;
  analysis_v2: AnalysisV2;
}

export class AnalysisV2 {
  id: number;
  analysisID: number;
  analysisName: string;
  analysisConfig?: AnalysisConfig;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  schemaVersion: number;
}

export class AnalysisConfig {
  primary_metrics: MerlionMetric[];
  secondary_metrics: MerlionMetric[];
  guardrail_metrics: MerlionMetric[];
  control_metrics?: MerlionMetric[];
}

export class MerlionMetric {
  name?: string;
  description?: string;
  owners?: string[];
  type?: string;
  dimensions?: string[];
  sources?: string[];
  platform?: string;
  query_engine?: string;
  link?: string;
  metadata?: Metadata;
  expr?: string;
  is_higher_better?: number;
  data_sensitivity?: string;
  metric_quality?: MetricQuality;
  version: number;
}

export class Metadata {
  display_name?: string;
  tags?: string;
  vertical?: string;
}

export class MetricQuality {
  last_refresh_date?: string;
}

/** ExperimentAnalysis is the DTO that we need for passing analysis configuration */
export class ExperimentAnalysis {
  analysisConfig?: ExperimentAnalysisConfig;
  schemaVersion?: number;
}

export class ExperimentAnalysisConfig {
  metrics?: AnalysisMetric[];
  guardrail_metrics?: MetricWithDimensionFields[];
  primary_metrics?: MetricWithDimensionFields[];
  secondary_metrics?: MetricWithDimensionFields[];
  control_metrics?: MetricWithDimensionFields[];
  deepdive_all_dimensions?: boolean;
  enable_triggered_analysis?: boolean;
  populationFilterEvent: string;
  populationFilterState: string;
}

export enum AnalysisMetricType {
  PreDefined = 'pre-defined',
  EventBased = 'event-based',
}

/** Analysis Metric defines a metric used in analysis */
export class AnalysisMetric {
  id?: string;
  name: string;
  type: AnalysisMetricType;
  valueType: string;
  parameters?: { [key: string]: string };
  isPrimaryMetric: boolean;
  description?: string;
}

export class ExperimentAnalysisConfigView {
  id?: string;
  analysisName?: string;
  analysisConfig?: ExperimentAnalysisConfig;
  schemaVersion?: number;
  supersetLink?: string;
}

/** ExperimentReview represents the reviewExperiment of an experiment. */
export class ExperimentReview {
  success: boolean;
  errors?: string[];
  warnings?: string[];
  suggestion?: Experiment;
  conflicts?: ExperimentPlan[];
  overlappedExperiments?: Experiment[];
  overlapSuggestion?: Experiment;
}

/** ExperimentPlan represents a schedule or conflict for an experiment. */
export class ExperimentPlan {
  start: Date;
  end: Date;
  population: number;
  experiments: Experiment[];
}

/** Slotting represents the slotting for the experiment. */
export class Slotting {
  byPercentage?: number;
  byAssignment?: number[];
  byGroup?: string;
}

/** Targeting represents a static/dynamic targeting with associated slotting if required. */
export class Targeting {
  constraint: Constraint;
  slotting: Slotting;
}

export class ExperimentMetadata {
  description?: string;
  productManager?: string;
  productAnalyst?: string;
  notifyToReschedule?: boolean;
  abortReason?: string;
  primaryMetricOptOutEnabled?: boolean;
  primaryMetricOptOutReason?: string;
  skipFacetCheck?: {
    [variable: string]: string; // <- reason
  };
  sampleSize?: SampleSize;
}

export class SampleSize {
  required?: SampleSizeRequirement;
  estimated?: SampleSizeEstimated;
  override?: boolean;
  reason?: string;
  overrideMaxAvailablePopulation?: boolean;
  overrideMaxAvailablePopulationReason?: string;
}

export class SampleSizeEstimated {
  variables?: VariableEstimate[];
  suggestedPercentage?: number;
  populationSize?: number | undefined | null;
}

export class SampleSizeEstimationRequest {
  variables: Variable[];
  constraints: Constraint[];
  requiredPerTreatment: number;
  requiredTotal: number;
}

export class SampleSizeEstimateResponse {
  data: {
    pre_n: number;
    sample_size: number;
    sample_cluster_size: number;
  };
}

export interface ExperimentMetric {
  metric: string;
  value: number | string | boolean;
}

export interface ExperimentMetricsDetails {
  experiment_metrics_details: ExperimentMetric[];
}

export interface SampleSizeRequirement {
  percent?: number;
  excludedVariables?: string[];
  baselineConversionRate?: number;
  minimumDetectableEffect?: number;
  relative?: boolean;
  isRatio?: boolean;
  powerLevel?: number;
  alphaLevel?: number;
  stddev?: number | undefined | null;
  interClusterStdDev?: number | undefined | null;
  intraClusterStdDev?: number | undefined | null;
  randomUnits?: number | undefined | null;
  observations?: number | undefined | null;
  sampleClusterSize?: number | undefined | null;
  variable?: Variable | undefined | null;
}

export class VariableEstimate {
  name: string;
  size: number;
}

export interface ExperimentAvailability {
  domain: string;
  restrictBy: string; // represents how the population is restricted by (city, ctry...)
  start: string;
  end: string;
  offsetInSec: string;
  availability: Availability;
}

export interface Availability {
  [date: string]: AvailabilityInfo;
}

export interface AvailabilityInfo {
  availability: number;
  partialOccupancy: boolean;
  status: string;
}

/** VariableInfo represents the metadata for the variable management. */
export class VariableInfo {
  static [immerable] = true;
  name: string;
  backend: string;
  techFamily: string;
  domain: string;
  desc: string;
  state: string;
  public: boolean;
  enabled: boolean = true;
  type: string;
  schema?: string;
  facets?: string[];
  version: number;
  owner?: string;
  scriptable: boolean;
  criticality: Criticality;
  lastRolloutUpdatedTime?: Date | null;
  hot: boolean;
  RestrictedBy?: string;
  schedulingDefinition?: SchedulingDef;
  assignBy?: string;
  metadata?: VariableMetadata;
  complexity?: ComplexityMetadata;
  complexityLevel?: string;
  productEntity?: string;
  xmsUpdatedAt?: Date | null;
  canary?: {
    enabled: boolean;
    phases: Array<{
      phase: number;
      percentage: number;
      duration_minutes: number;
    }>;
    metrics: Array<{
      name: string;
      query: string;
      thresholds: {
        min: number;
        max: number;
      };
    }>;
    auto_rollback: boolean;
    auto_advance: boolean;
    notify_email: string;
  };
  // OVO Sync fields
  sourceTenantSeq?: number; // Indicates synced from which tenant (e.g., 1 = Grab)
  targetTenantSeqs?: number[]; // Indicates synced to which tenants (e.g., [3] = OVO)
}

/** Criticality represents the criticality information of a variable. */
export enum Criticality {
  Critical = 1,
  NonCritical = 0,
}

export class VariableMetadata {
  productOwner?: string;
  technicalOwner?: string;
  labels?: string[];
  documentUrl?: string;
  jsonSchema?: string;
  subscribedServices?: string[];
  mandatoryFacets?: string[];
  contextualRules?: ContextualRule[];
  managedByPlatform?: string;
  canaryConfig?: {
    enabled: boolean;
    disabledReason?: string;
    disabledReasonNote?: string;
    phases: Array<{
      targetPercentage: number;
      durationMins: number;
    }>;
    // Serialised form: query and monitor rows are merged into one array
    // differentiated by `type`. UI stores them separately before serialisation.
    metrics: Array<{
      type?: 'query' | 'monitor';
      displayName?: string;
      query?: string;
      min?: number;
      max?: number;
      monitorUrl?: string;
      monitorId?: number;
    }>;
    // Legacy field — kept for reading old saved data during migration.
    monitors?: Array<{
      displayName?: string;
      monitorUrl?: string;
    }>;
  };
}
export interface ContextualRule {
  contextualRulesConditions: ContextualCondition[];
  minimumLength?: number;
  maximumLength?: number;
  minimumValue?: number;
  maximumValue?: number;
  allowedValues?: string[] | number[];
  jsonSchema?: string;
}
export interface ContextualCondition {
  target: string;
  op: string;
  value: string;
}

export class ComplexityMetadata {
  violationCount?: number;
  level?: string;
  complexityFactors?: ComplexityFactorsMetadata;
  guardrails?: ComplexityFactorsMetadata;
}

export class ComplexityFactorsMetadata {
  [factorName: string]: FactorMetadata;
}

export class FactorMetadata {
  label?: string;
  violated?: boolean;
  waivable?: boolean;
  key?: string;
  exceptionRaised?: boolean;
  exceptionReason?: string;
  [factorKey: string]: any;
}

/** Report of an experiment, contains multiple metrics */
export class Report {
  [metricName: string]: ReportMetric;
}

/** Report of a metric, contains multiple variants */
export class ReportMetric {
  type: string;
  report: ReportVariants;
}

export interface FriendlyRole {
  id: string;
  team: string;
  role: string;
  color: string;
}

/**
 * @example [{
 *    roles: [ 'Default' ],
 *    userName: 'john.doe@grabtaxi.com',
 *  }]
 */
export interface UserRoles {
  roles: string[];
  userName: string;
}

export interface User {
  Id: string;
  Email: string;
}

/**
 * @example {
 *    email: 'john.doe@grabtaxi.com',
 *    metric: {},
 *    variable: {
 *        discountAlgo: [ 'read' ],
 *        dynamicSurchargeExp: [ 'read' ]
 *     },
 *     managersRoles: [ 'DaxapiFullAccess' ]
 *   }
 *
 *   managersRoles - list of roles that user can give to other users, initially configured via 'Admiral' app.
 */
export interface CurrentUser {
  email?: string;
  metric?: {};
  variable?: { [variableName: string]: string[] };
  managersRoles?: string[];
  roles?: string[];
}

export interface ReportVariants {
  [variantName: string]: any;
}

/** Report of a variant */
export class ReportVariantVariance {
  count: number;
  mean: number;
  sum: number;
  isControl: boolean;
  delta?: number;
  pValue?: number;
  significant?: boolean;
}

export class ReportVariantProportion {
  count: number;
  isControl: boolean;
  groups: ReportGroups;
  significant?: boolean;
}

export interface ReportGroups {
  [key: string]: number;
}

/** RolloutType represents a type of a rollout. */
export type RolloutType =
  | 'none'
  | 'simple'
  | 'hybrid'
  | 'percentage'
  | 'whitelist'
  | 'blacklist'
  | 'percentageOrList';

export interface RolloutTarget {
  constraints?: Constraint[];
  title?: string; // UI-only title
  type?: RolloutType;
  string?: string;
  value: number;
  script?: string;
  id?: string;
  reorder?: boolean;
  index?: number;
}

export interface DomainMetadata {
  name: string;
  title: string;
  description: string;
  active: boolean;
  strategies: string[];
  family: string;
  targetBy?: string;
  restrictBy?: string;
  overlapping?: boolean;
}

export interface CountInfo {
  granularity: number; // count taken every x hours
  total?: number; // total count for given granularity
  groupBy: string; // represents the facet on which the count is being grouped by. For ex - city, device etc
  values?: { [facet: string]: number }; // the map of facet - count
}

export interface Event {
  scope: string;
  event: string;
  stateName: string;
  mappedName?: string; // friendy name or standard name given by PAs
  description?: string; // describes the event's purpose and usage
  src: string; // source is either pax, dax for mobile events, backend service name for backend events
  facets?: string[];
  appVersions?: string[];
  ost?: string; // OS type of device, Android, IOS, null for backend
  techFamily?: string; // Family represents the tech family the event belongs to
  isQEM: boolean; // represents if event is engineering or business event
  isUserTriggered: boolean; // represents if event is UserTriggered or system triggered
  metrics?: string[]; // represents the metrics which use this event
  count?: CountInfo; // count info of the event
  owner?: string; // Owner for the definition in ExP
  lastIngestionDate?: string; // The date on which the events were ingested in our pipeline
}

// Variant can be rollout target or experiment variant
export interface Variant {
  id: string;
  title: string;
}

// MessageConfig represents a template with values for its' parameters
export interface MessageConfig {
  templateID?: string;
  params?: AttributeValues | undefined | null;
}

export interface AttributeValues {
  [language: string]: {
    [key: string]: string;
  };
}

// MessageGroup represents a collection of variants and the configured message for them
export interface MessageGroup {
  variantIDs: string[];
  status?: string;
  messageCfg?: MessageConfig;
}

// Campaign is set of time bounded message triggers for experiment or rollout variants
export interface Campaign {
  name: string;
  type: 'experiment' | 'rollout';
  variable?: string;
  experiment?: string | null;
  events: string[];
  start?: Date | null;
  end?: Date | null;
  status?: string;
  messageGroups: MessageGroup[];
  owner?: string;
}

// The message template entry a entry of template list retrieved from hedwig
export interface MessageTemplateEntry {
  id: string;
  name: string;
  templateType?: string;
}

export interface MessageTemplate extends MessageTemplateEntry {
  variants: AttributeValues;
}

export interface BlackdayMetadata {
  type: string;
  country: string;
  cities: string[];
}

export interface Blackday {
  id: number;
  name: string;
  description: string;
  owner: string;
  state: string;
  metadata: BlackdayMetadata;
  created_at?: string;
  updated_at?: string;
  start_time: string;
  end_time: string;
}

export interface BlackdayResponse {
  id: number;
  name: string;
  description: string;
  owner: string;
  state: string;
  metadata: BlackdayMetadata;
  created_at?: string;
  updated_at?: string;
  start_time: string;
  end_time: string;
}

// The rollout diff created after making changes to a rollout. This will be used to approve rollout changes
export interface RolloutDiff {
  oldRollout: Rollout;
  newRollout: Rollout;
  id: string;
  expireAt: string;
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
  updatedBy?: string;
  state: string;
  resource?: string;
  description?: string;
  requestLink?: string;
}

// The rollout diff create request
export interface RolloutDiffCreateRequest {
  oldRollout: Rollout;
  newRollout: Rollout;
  description: string;
  requestLink?: string;
}

export interface VariableStatsView {
  name: string;
  scopes?: { [scope: string]: ScopeStatsView };
  updated_at?: string;
  data_at?: string;
}

export interface ScopeStatsView {
  count: number;
  facets: { [facet: string]: FacetView };
}

export interface FacetView {
  cardinality: number;
  values?: {};
}

export interface SummaryEntry {
  sampleSize: number;
  purity: number;
}

export interface VariantInfo {
  id: string;
  sval: string;
  val: number;
  order: number;
}

export interface InterferenceEntry {
  experiment: string;
  variable: string;
  variantID: string;
  interference: number;
}

export interface InterferenceView {
  variants: {
    [variableName: string]: {
      [variantId: string]: VariantInfo;
    };
  };
  summary: {
    [variantId: string]: SummaryEntry;
  };
  interferences: {
    [variantId: string]: InterferenceEntry[];
  };
}

export interface PreDefinedMetricEntry {
  id: string;
  name: string;
  description: string;
  notebook: string;
  owner?: string;
  valueType: string;
  vertical: string;
}

export interface PreDefinedMetricEntryMap {
  [details: string]: PreDefinedMetricEntry;
}

export interface AnalysisReportEntry {
  embedToken: string;
  embedURL: string;
  expiration: string;
  groupID: string;
  reportID: string;
  name: string;
}

export interface ApprovalFlow {
  status: string;
  resources: string[];
  data: ApprovalFlowViewModel[];
}

export interface ApprovalFlowViewModel {
  email: string;
  actionStr: string;
  comments: string;
  timestamp: string;
  resource: string;
}

export interface UpdateStateResponse {
  scheduled: boolean;
}

export interface Distribution {
  type: string;
  lowerBound: string;
  upperBound: string;
  mean?: string;
  sigma?: string;
  n?: string;
  p?: string;
  rate?: string;
  lambda?: string;
}

export interface AutoAdaptiveParam {
  precision?: string;
  sampleSize?: string;
  distributions?: { [distributionTarget: string]: Distribution };
  samplingAlgorithm: string;
}

export interface AutoAdaptive {
  name: string;
  parametersDef: { [variableName: string]: AutoAdaptiveParam };
  duration: number;
}

export interface SchedulingDef {
  is_flexible: boolean;
  duration_in_days?: number;
  priority?: number;
  search_range_in_days?: number;
  conditions?: Condition[];
  preferences?: Preference[];
  tags?: SchedulingTag[];
}

export interface Condition {
  key: string;
  operator: string;
  value: string;
}

export interface SchedulingTag {
  key: string;
  values: string[];
}

export interface Preference {
  key: string;
  value: string;
}
export interface Policy {
  id: number;
  policyName: string;
  policyDesc: string;
}

export interface TFRole {
  tf: string;
  role: string;
}

export interface UserRoleScopeMapping {
  policyNames?: string[];
  roleName?: string;
  service?: string;
  tf?: string;
}

export interface MetricParams {
  display_name?: string;
  description?: string;
  owner?: string;
  platform?: string;
}

export interface ProductEntity {
  name: string;
  variableCount?: number;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
}

export interface getTechFamilyApproversRequest {
  tech_family: string[];
}
